import React, { useCallback, useEffect, useState } from 'react';
import { useForm, type RegisterOptions } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { useUnsavedChangeActions } from '@/hooks/useScopeChangeHooks';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { cn } from '@/lib/utils';
import { InlineErrorToast, InlineSuccessToast } from './InlineToasts';
import { useClearableTimeout } from '@/hooks/useClearableTimeout';

interface ActionInputProps {
  saveAction: (value: string) => Promise<unknown>;
  label: string;
  scopeId?: string;
  isSaveLoading?: boolean;
  defaultValue?: string;
  showInlineToast?: boolean;
  className?: string;
  validator?: RegisterOptions<FormState, 'value'>;
  inputClassName?: string;
}

interface FormState {
  value: string;
}

interface FormUIState {
  isEditing: boolean;
  inputDisabled: boolean;
}

const MAX_TOAST_DURATION = 4000;

type StatusState = { type: 'idle' | 'success' | 'error'; value?: string };

export const ActionInput: React.FC<ActionInputProps> = ({
  saveAction: action,
  label,
  isSaveLoading = false,
  defaultValue = '',
  showInlineToast = true,
  className = '',
  scopeId,
  validator,
  inputClassName,
}) => {
  const inputId = scopeId ? `action-input-${scopeId}` : 'action-input';
  const [status, setStatus] = useState<StatusState>({ type: 'idle' });
  // Use the clearable timeout hook for toast status
  const [startToastTimeout] = useClearableTimeout(
    () => setStatus({ type: 'idle' }),
    MAX_TOAST_DURATION
  );
  const [formUIState, setFormUIState] = useState<FormUIState>({
    isEditing: false,
    inputDisabled: true,
  });
  const { markDirty, markClean } = useUnsavedChangeActions();
  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
    setFocus,
  } = useForm<FormState>({
    defaultValues: { value: defaultValue },
    mode: 'onChange',
  });

  const hasValueErrors = !!errors.value;
  const actionsDisabled = isSaveLoading;
  const registeredField = register('value', validator);

  const startEditing = useCallback(() => {
    setFormUIState({ isEditing: true, inputDisabled: false });
    // delay focus to next frame to ensure input is enabled
    requestAnimationFrame(() => {
      setFocus('value');
    });
  }, [setFocus]);

  const stopEditing = useCallback(() => {
    setFormUIState({ isEditing: false, inputDisabled: true });
    const activeElement = document.activeElement as HTMLElement | null;
    if (activeElement) {
      activeElement.blur();
    }
  }, []);

  // Only set status in these callbacks; timeout logic is handled by the hook
  const showSuccessToast = useCallback(() => {
    if (!showInlineToast) return;
    setStatus({ type: 'success' });
    startToastTimeout();
  }, [showInlineToast, startToastTimeout]);

  const showErrorToast = useCallback(
    (message?: string) => {
      if (!showInlineToast) return;
      setStatus({ type: 'error', value: message });
      startToastTimeout();
    },
    [showInlineToast, startToastTimeout]
  );

  useEffect(() => {
    if (!scopeId) return;
    if (isDirty) {
      markDirty(scopeId);
    } else {
      markClean(scopeId);
    }
    return () => {
      if (scopeId) markClean(scopeId);
    };
  }, [isDirty, markDirty, scopeId, markClean]);

  useEffect(() => {
    // This syncs server updates into the field when not editing
    const canReset = !formUIState.isEditing && !isDirty;
    if (canReset) {
      reset({ value: defaultValue });
    }
  }, [defaultValue, reset, formUIState.isEditing, isDirty]);

  const onSave = async (data: { value: string }) => {
    try {
      reset({ value: data.value }, { keepDirty: false });
      await action(data.value);
      showSuccessToast();
      stopEditing();
    } catch (e: Error | unknown) {
      reset({ value: data.value });
      const message = e instanceof Error ? e.message : undefined;
      showErrorToast(message);
    }
  };

  const onCancel = () => {
    reset({ value: defaultValue }, { keepDirty: false });
    stopEditing();
  };

  const showChangeActions = formUIState.isEditing;
  const handleOnSave = handleSubmit(onSave);
  const blockSubmit = actionsDisabled || hasValueErrors;
  const showServerSuccess = status.type === 'success' && !hasValueErrors;
  const showServerError = status.type === 'error' && !hasValueErrors;

  return (
    <div className={cn('flex gap-2 items-end w-full', className)}>
      <form
        onSubmit={e => {
          e.preventDefault();
          if (blockSubmit) return;
          handleOnSave(e);
        }}
        className="flex-1 flex flex-col gap-2"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 w-full">
          <Label className="text-sm font-medium" htmlFor={inputId}>
            {label}
          </Label>
          {hasValueErrors && <InlineErrorToast label={errors.value?.message} />}
          {showServerSuccess ? (
            <InlineSuccessToast label="Changes Saved" />
          ) : showServerError ? (
            <InlineErrorToast label={status.value ?? 'Error Saving Changes'} />
          ) : null}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex-1">
            <Input
              {...registeredField}
              disabled={formUIState.inputDisabled || isSaveLoading}
              id={inputId}
              className={cn('w-full lg:w-3/5 xl:w-1/2', inputClassName)}
              aria-invalid={hasValueErrors}
            />
          </div>
          {showChangeActions && (
            <div className="flex gap-2 mt-2 md:mt-0 md:flex-row">
              <Button variant="default" disabled={blockSubmit} type="submit">
                {isSaveLoading ? 'Saving...' : 'Save'}
              </Button>
              <Button
                onClick={onCancel}
                variant="outline"
                disabled={actionsDisabled}
                type="button"
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </form>
      {!showChangeActions && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={startEditing} type="button">
            Edit
          </Button>
        </div>
      )}
    </div>
  );
};
